const API_BASE = import.meta.env.DEV 
  ? 'http://localhost:8787' 
  : 'https://reactor-jarvis-app.valleyintest.workers.dev';

const TOKEN_KEY = 'jarvis_token';

interface ApiOptions extends RequestInit {
  json?: unknown;
}

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// トークン管理
export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  remove: () => localStorage.removeItem(TOKEN_KEY),
};

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { json, ...init } = options;
  const token = tokenStorage.get();
  
  const config: RequestInit = {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...init.headers,
    },
  };
  
  if (json) {
    config.body = JSON.stringify(json);
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, config);
  
  const data = await response.json();
  
  if (!response.ok) {
    // 401の場合はトークンをクリア
    if (response.status === 401) {
      tokenStorage.remove();
    }
    throw new ApiError(response.status, data.message || data.error || 'エラーが発生しました');
  }
  
  return data as T;
}

// Auth API
export const auth = {
  login: async (email: string, password: string) => {
    const result = await request<{ success: boolean; token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      json: { email, password },
    });
    // トークンをlocalStorageに保存
    if (result.token) {
      tokenStorage.set(result.token);
    }
    return result;
  },
  
  logout: async () => {
    const result = await request<{ success: boolean }>('/api/auth/logout', { method: 'POST' });
    // トークンを削除
    tokenStorage.remove();
    return result;
  },
  
  me: () => 
    request<User>('/api/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ success: boolean }>('/api/auth/change-password', {
      method: 'POST',
      json: { currentPassword, newPassword },
    }),
  
  resetPasswordRequest: (email: string) =>
    request<{ success: boolean }>('/api/auth/reset-password-request', {
      method: 'POST',
      json: { email },
    }),
  
  validateInvite: (token: string) =>
    request<{ valid: boolean; email: string; role: string }>(`/api/auth/invite/${token}`),
  
  acceptInvite: async (token: string, name: string, password: string) => {
    const result = await request<{ success: boolean; token: string; user: User }>(`/api/auth/invite/${token}/accept`, {
      method: 'POST',
      json: { name, password },
    });
    // トークンをlocalStorageに保存
    if (result.token) {
      tokenStorage.set(result.token);
    }
    return result;
  },
};

// AI Models API
export const aiModels = {
  list: () =>
    request<AIModelsResponse>('/api/sessions/ai-models'),
};

// Sessions API
export const sessions = {
  start: (data: StartSessionData) =>
    request<StartSessionResponse>('/api/sessions/start', {
      method: 'POST',
      json: data,
    }),
  
  kyc: (sessionId: string, data: KycData) =>
    request<{ success: boolean; message: string }>(`/api/sessions/${sessionId}/kyc`, {
      method: 'POST',
      json: data,
    }),
  
  initialIdea: (sessionId: string, idea: string) =>
    request<InitialIdeaResponse>(`/api/sessions/${sessionId}/initial-idea`, {
      method: 'POST',
      json: { idea },
    }),
  
  answer: (sessionId: string, questionId: string, answer: string) =>
    request<AnswerResponse>(`/api/sessions/${sessionId}/answer`, {
      method: 'POST',
      json: { questionId, answer },
    }),
  
  pause: (sessionId: string) =>
    request<{ success: boolean; message: string }>(`/api/sessions/${sessionId}/pause`, {
      method: 'POST',
    }),
  
  resume: (sessionId: string) =>
    request<ResumeResponse>(`/api/sessions/${sessionId}/resume`, {
      method: 'POST',
    }),
  
  get: (sessionId: string) =>
    request<SessionDetail>(`/api/sessions/${sessionId}`),
  
  list: () =>
    request<Session[]>('/api/sessions'),
  
  generateBlueprint: (sessionId: string) =>
    request<BlueprintResponse>(`/api/sessions/${sessionId}/generate-blueprint`, {
      method: 'POST',
    }),
  
  // OpenAI Realtime APIセッション作成
  createRealtimeSession: () =>
    request<RealtimeSessionResponse>('/api/sessions/realtime/session', {
      method: 'POST',
    }),
};

// Users API
export const users = {
  list: () =>
    request<User[]>('/api/users'),
  
  get: (id: string) =>
    request<User>(`/api/users/${id}`),
  
  invite: (email: string, role: string) =>
    request<{ success: boolean }>('/api/users/invite', {
      method: 'POST',
      json: { email, role },
    }),
  
  update: (id: string, data: Partial<User>) =>
    request<User>(`/api/users/${id}`, {
      method: 'PUT',
      json: data,
    }),
  
  delete: (id: string) =>
    request<{ success: boolean }>(`/api/users/${id}`, {
      method: 'DELETE',
    }),
  
  updateProfile: (data: { name?: string; avatarUrl?: string }) =>
    request<User>('/api/users/me/profile', {
      method: 'PUT',
      json: data,
    }),
};

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'viewer';
  status: 'active' | 'pending' | 'disabled';
  avatarUrl?: string;
  isMaster?: boolean;
  lastLoginAt?: string;
}

export interface Session {
  id: string;
  userId?: string;
  guestEmail?: string;
  guestName?: string;
  initialIdea?: string;
  status: 'in_progress' | 'paused' | 'completed' | 'cancelled';
  totalQuestions: number;
  answeredQuestions: number;
  createdAt: string;
}

export interface Question {
  id: string;
  number: number;
  category: 'why' | 'who' | 'what' | 'flow' | 'vibe';
  text: string;
}

export interface StartSessionData {
  guestEmail?: string;
  guestName?: string;
  guestCompany?: string;
  guestPhone?: string;
  aiModel?: AIProvider;
  aiModelId?: AIModelId;
}

export interface StartSessionResponse {
  sessionId: string;
  greeting: string;
  isKnownContact: boolean;
  needsKyc: boolean;
}

export interface KycData {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  relationship?: string;
}

export interface InitialIdeaResponse {
  message: string;
  currentQuestion: Question;
  totalQuestions: number;
}

export interface AnswerResponse {
  message: string;
  answeredQuestions: number;
  totalQuestions: number;
  isComplete: boolean;
  nextQuestion: Question | null;
}

export interface ResumeResponse {
  success: boolean;
  message: string;
  currentQuestion: Question | null;
}

export interface SessionDetail extends Session {
  questions: Array<{
    id: string;
    questionNumber: number;
    category: string;
    questionText: string;
    isAnswered: boolean;
  }>;
  conversationLogs: Array<{
    id: string;
    role: 'jarvis' | 'user';
    content: string;
    createdAt: string;
  }>;
}

export interface BlueprintResponse {
  blueprintId: string;
  title: string;
  markdown: string;
}

export interface RealtimeSessionResponse {
  success: boolean;
  sessionId: string;
  clientSecret: string;
  expiresAt: number;
  websocketUrl: string;
}

export type AIModelId = 
  | 'gpt-4o' 
  | 'gpt-4o-mini' 
  | 'gpt-4-turbo' 
  | 'gpt-3.5-turbo'
  | 'gpt-4o-realtime'
  | 'gemini-2.0-flash' 
  | 'gemini-1.5-pro' 
  | 'gemini-1.5-flash';

export type AIProvider = 'openai' | 'gemini';

export interface AIModel {
  id: AIModelId;
  name: string;
  description: string;
  provider: AIProvider;
  isDefault: boolean;
  supportsRealtime?: boolean;
}

export interface AIModelsResponse {
  currentModel: AIModelId | null;
  models: AIModel[];
  realtimeAvailable: boolean;
  error?: string;
}

export { ApiError };

